
import DexieDB from './DexieDB';
import DexieDBProvider from './DexieDBProvider';
import { AppSettings } from '@/models/settings/AppSettings';
import { LoggerService } from '@/utils';
import { EditorModeEnums } from '@/enums/EditorModeEnums';
import { LiteEvent } from '@/models/event/liteEvent';

class AppSettingsProvider {
    /**
     * use this only for special navigation cases,
     * e.g. quick edit save --> open table.
     */
    private navigateTo?: string;

    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;
    private appSettings: AppSettings = new AppSettings();
    private readonly onAppSettingsLoaded = new LiteEvent<AppSettings>();
    private appSettingsLoaded = false;


    // tslint:disable-next-line: no-empty
    constructor() { }

    /**
     * Put/Update a AppSettings
     * @param inAppSettings AppSettings
     */
    public async PutAppSettings(inAppSettings: AppSettings): Promise<number> {
        return this.dexieDB.put<AppSettings>(this.dexieDB.getTables().appSettings, inAppSettings);
    }

    /**
     * Get app settings
     */
    public async FetchAppSettings() {
        const table = this.dexieDB.getTable<AppSettings>(this.dexieDB.getTables().appSettings);
        if (table !== undefined) {
            await table.get({ id: 1 })
                .then(async (data) => {
                    if (data === undefined) {
                        const newAppSettings = new AppSettings();
                        await this.PutAppSettings(newAppSettings);
                        this.appSettings = newAppSettings;
                    } else {
                        this.appSettings = new AppSettings(data);
                    }
                    this.appSettingsLoaded = true;
                    this.onAppSettingsLoaded.trigger(this.appSettings);
                })
                .catch((error) => {
                    LoggerService.LogError(`Error on FetchtAppSettings: `, error);
                });
        } else {
            LoggerService.LogError(`Error on loading AppSettings from dexieDB`);
        }
    }

    /**
     * Method to set last fetched event ID from server
     * @param inId A number for last event ID
     */
    public ChangeLastEventId(inId: number) {
        this.appSettings.lastEventId = inId;
        this.PutAppSettings(this.appSettings)
            .catch((error) => {
                LoggerService.LogError(`Error on saving app settings:`, error);
            });
        this.appSettingsLoaded = true;
        this.onAppSettingsLoaded.trigger(this.appSettings);
    }

    /**
     * Method to set last sync timestamp
     * @param message Optional message string
     */
    public UpdateLastSync(message?: string) {
        this.appSettings.lastSync = (new Date()).toISOString();
        if (message !== undefined) {
            this.appSettings.lastSyncMessage = message;
        } else {
            this.appSettings.lastSyncMessage = undefined;
        }
        this.PutAppSettings(this.appSettings)
            .catch((error) => {
                LoggerService.LogError(`Error on saving app settings:`, error);
            });
    }

    /**
     * Method to set last loggedin user id.
     * @param inUserId  UserId string or leave empty to clear
     */
    public ChangeLastLoggedInUserId(inUserId?: string) {
        this.appSettings.lastLoggedInUserId = inUserId;
        this.PutAppSettings(this.appSettings)
            .catch((error) => {
                LoggerService.LogError(`Error on saving app settings:`, error);
            });
    }

    public SetAutoSync(autoSync: boolean) {
        this.appSettings.autoSync = autoSync;
        this.PutAppSettings(this.appSettings)
            .catch((error) => {
                LoggerService.LogError(`Error on saving app settings(SetAutoSync):`, error);
            });
    }

    public SetSnapshotFetched(inSnapshotFetched: string) {
        this.appSettings.snapshotFetched = inSnapshotFetched;
    }

    public SetLastEventId(inLastEventId: number) {
        this.appSettings.lastEventId = inLastEventId;
    }

    public SetFirstStart(inFirstStart: boolean) {
        this.appSettings.firstStart = inFirstStart;
    }

    public SetTBlenMatrialUuidChanged(inTBlenMatrialUuidChanged: boolean) {
        this.appSettings.tBlenMatrialUuidChanged = inTBlenMatrialUuidChanged;
    }

    public SetDirectOpenId(inDirectOpenId: string | undefined) {
        this.appSettings.directOpenId = inDirectOpenId;
    }

    get GetAppSettings(): AppSettings {
        return this.appSettings;
    }

    /**
     * Event getter for app settings
     */
    public get OnAppSettingsLoaded() {
        return this.onAppSettingsLoaded.expose();
    }

    public triggerUpdate() {
        this.onAppSettingsLoaded.trigger(this.appSettings);
    }

    get GetAppSettingsLoaded() {
        return this.appSettingsLoaded;
    }

    /**
     * Setter to set the navigateTo value.
     * Use this only for special navigation cases!
     */
    set SetNavigateTo(to: string | undefined) {
        this.navigateTo = to;
    }
    /**
     * Getter to get the navigateTo value
     * Use this only for special navigation cases!
     */
    get GetNavigateTo() {
        return this.navigateTo;
    }
}

export default AppSettingsProvider;
